import { createApiRef, OAuthApi } from '@backstage/core-plugin-api';
import type { Entity } from '@backstage/catalog-model';
import { Octokit } from '@octokit/rest';
import yaml from 'js-yaml';
import type { AzureManagementServiceApi } from '@runway/devkit-common';
import type { AIFoundryMapping } from '../components/AIFoundrySelector/types';
import type { AIOneTrustServiceApi } from './AIOneTrust.service';

// ── Constants ─────────────────────────────────────────────────────────────────

/** GitHub organisation used for all repository operations */
export const GITHUB_ORG = 'AAInternal';

/** Repository containing the GaaS policies and AI configs */
export const GITHUB_REPO = 'GaaS-Policy_as_Code';

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * Minimal subset of the catalog-projects API needed for app short name
 * resolution. Avoids a hard dependency on the catalog-customizations plugin.
 */
export interface AppShortnameResolver {
  getRepoCustomProperties(entity: Entity): Promise<{ appShortName: string }>;
}

/** Parameters required to provision AI resources */
export interface ProvisionProdResourcesParams {
  entityName: string;
  appShortname: string;
  aiCategory: string;
  foundry: AIFoundryMapping;
  aadGroup: string;
}

export type ProvisionResourcesEnvironment = 'prod' | 'nonprod';

export interface ProvisionResourcesParams extends ProvisionProdResourcesParams {
  environment: ProvisionResourcesEnvironment;
}

// ── Pure utility functions ────────────────────────────────────────────────────

/**
 * Extracts a human-readable error message from an unknown caught error.
 *
 * @param error - The caught error value
 * @returns A descriptive error string
 */
export const toErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'An unknown error occurred';

// ── Service interface & API ref ───────────────────────────────────────────────

/**
 * Client-side service for AI provisioning operations.
 *
 * Manages authenticated GitHub file access (following the same auth pattern as
 * {@link GithubEnterpriseClientGraph}) and orchestrates the multi-step
 * provisioning flow.
 */
export interface AIProvisioningServiceApi {
  /** Fetches and base64-decodes a file from a GitHub repository. */
  fetchGitHubFile(repo: string, path: string): Promise<string>;

  /**
   * Resolves the CMDB app short name via a two-tier lookup:
   *   1. GitHub custom properties → 2. aa.yaml
   *
   * @throws Error if the app short name cannot be resolved from either source
   */
  resolveAppShortname(resolver: AppShortnameResolver, entity: Entity, entityName: string): Promise<string>;

  /** Fetches and parses the AI configuration YAML for the given app/category. */
  fetchAIConfig(appShortname: string, aiCategory: string): Promise<Record<string, unknown>>;

  /**
   * Orchestrates AI Foundry project creation and workflow dispatches.
   */
  provisionResources(params: ProvisionResourcesParams, amsApi: AzureManagementServiceApi): Promise<void>;

  /**
   * Orchestrates the full production provisioning flow:
   *   1. Create AI Foundry project
   *   2. Trigger workflow dispatches
   *
   * @throws Error if project creation or workflow dispatch fails
   */
  provisionProdResources(params: ProvisionProdResourcesParams, amsApi: AzureManagementServiceApi): Promise<void>;
}

export const AIProvisioningServiceApiRef = createApiRef<AIProvisioningServiceApi>({
  id: 'plugin.devkit.ai-provisioning.service',
});

// ── Service implementation ────────────────────────────────────────────────────

/**
 * Implements {@link AIProvisioningServiceApi}.
 *
 * Constructor follows the same auth-injection pattern used by
 * `GithubEnterpriseClientGraph` in `github.service.ts` — the GitHub API URL
 * and OAuth provider are injected once, and authenticated Octokit instances
 * are created on demand.
 */
export class AIProvisioningService implements AIProvisioningServiceApi {
  constructor(
    private readonly ghecApiUrl: string,
    private readonly ghecAuthApi: OAuthApi,
    private readonly oneTrustApi: AIOneTrustServiceApi,
  ) {}

  /** Creates an authenticated Octokit REST client (reuses devkit's auth pattern). */
  private async getOctokit(): Promise<Octokit> {
    const token = await this.ghecAuthApi.getAccessToken();
    return new Octokit({ auth: token, baseUrl: this.ghecApiUrl });
  }

  async fetchGitHubFile(repo: string, path: string): Promise<string> {
    const octokit = await this.getOctokit();
    const response = await octokit.repos.getContent({
      owner: GITHUB_ORG,
      repo,
      path,
    });

    if (!('content' in response.data) || typeof response.data.content !== 'string') {
      throw new Error(`Invalid file content received from GitHub for ${path}`);
    }

    return Buffer.from(response.data.content, 'base64').toString('utf-8');
  }

  async resolveAppShortname(resolver: AppShortnameResolver, entity: Entity, entityName: string): Promise<string> {
    const { appShortName } = await resolver.getRepoCustomProperties(entity);

    if (!appShortName) {
      throw new Error(
        `Could not resolve the App Short Name for the application "${entityName}". ` +
          'Please ensure the repository has an appShortName set via GitHub custom properties.',
      );
    }

    return appShortName;
  }

  async fetchAIConfig(appShortname: string, aiCategory: string): Promise<Record<string, unknown>> {
    const content = await this.fetchGitHubFile(GITHUB_REPO, `ai/${appShortname}-${aiCategory}.yaml`);
    const parsed = yaml.load(content);

    if (!parsed || typeof parsed !== 'object') {
      throw new Error('AI config YAML is empty or invalid');
    }

    return parsed as Record<string, unknown>;
  }

  private isDuplicateFoundryProjectError(error: unknown): boolean {
    return toErrorMessage(error).toLowerCase().includes('already exists');
  }

  async provisionResources(params: ProvisionResourcesParams, amsApi: AzureManagementServiceApi): Promise<void> {
    const { entityName, appShortname, foundry, aadGroup, environment } = params;
    const environmentLabel = environment === 'prod' ? 'Production' : 'Non-production';
    const environmentLowerLabel = environment === 'prod' ? 'production' : 'non-production';

    if (environment === 'prod') {
      const policy = await this.oneTrustApi.fetchOneTrustPolicy();
      if (!policy.approvedAppShortNames.includes(appShortname)) {
        throw new Error(
          `Cannot provision production AI resources: application "${appShortname}" has not been approved/assessed by OneTrust for AI resource provisioning in production.`,
        );
      }
    }

    try {
      await amsApi.createAIFoundryProject({
        projectName: entityName,
        foundryResourceName: foundry.foundryName,
        description: `${environmentLabel} AI Foundry project for ${entityName}`,
        resourceGroupName: foundry.resourceGroupName,
        subscriptionId: foundry.subscriptionId,
        tenant: 'p',
        appShortname,
        env: environment,
        dataClassification: '',
        aad_group: aadGroup,
      });
    } catch (error) {
      if (!this.isDuplicateFoundryProjectError(error)) {
        throw new Error(`Failed to create ${environmentLowerLabel} AI Foundry project: ${toErrorMessage(error)}`);
      }
    }

    try {
      const octokit = await this.getOctokit();
      const publishApiInputs = {
        ENVIRONMENT: environment,
        COMMIT_ID_CHOICE: 'publish-all-artifacts-in-repo',
        'KEY-TRIGGER': 'yes',
      };

      await octokit.rest.actions.createWorkflowDispatch({
        owner: GITHUB_ORG,
        repo: entityName,
        workflow_id: 'publish-api.yaml',
        ref: 'main',
        inputs: publishApiInputs,
      });

      await octokit.rest.actions.createWorkflowDispatch({
        owner: GITHUB_ORG,
        repo: entityName,
        workflow_id: 'hidden-layer.yaml',
        ref: 'main',
        inputs: {
          releaseENV: environment,
        },
      });
    } catch (error) {
      throw new Error(
        `${environmentLabel} AI Foundry project "${entityName}" was created, but the APIM configuration setup failed: ${toErrorMessage(error)}`,
      );
    }
  }

  async provisionProdResources(params: ProvisionProdResourcesParams, amsApi: AzureManagementServiceApi): Promise<void> {
    return this.provisionResources({ ...params, environment: 'prod' }, amsApi);
  }
}
